(function () {
  function initVokalScrollFilm(root) {
    if (!root || root.dataset.vokalReady === "true") return;

    const video = root.querySelector("[data-vokal-hero-video]");
    const meterBar = root.querySelector("[data-vokal-meter-bar]");
    const timecode = root.querySelector("[data-vokal-timecode]");
    const loading = root.querySelector("[data-vokal-loading]");
    const shade = root.querySelector("[data-vokal-shade]");
    const panels = Array.from(root.querySelectorAll("[data-vokal-panel]"));
    const splitOverlay = root.querySelector("[data-vokal-split-overlay]");
    const topNav = root.querySelector(".vokal-top-nav");
    const menuToggle = root.querySelector("[data-vokal-menu-toggle]");
    const mobileMenu = root.querySelector("[data-vokal-mobile-menu]");

    if (!video || !meterBar || !timecode || !loading || !shade) return;

    root.dataset.vokalReady = "true";

    let duration = 1;
    let hasMetadata = false;
    let targetVideoProgress = 0;
    let smoothVideoProgress = 0;
    let scrollProgress = 0;
    let lastPanelScrollProgress = -1;
    let rafId = null;
    let lastSeekAt = 0;
    let hasPrimedVideo = false;
    let isActive = true;
    let isMobile = window.matchMedia("(max-width: 720px)").matches;

    const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
    const lerp = (from, to, amount) => from + (to - from) * amount;
    const easeOut = (value) => 1 - Math.pow(1 - value, 3);
    const smoothstep = (edge0, edge1, value) => {
      const t = clamp((value - edge0) / (edge1 - edge0));
      return t * t * (3 - 2 * t);
    };

    function getScrollProgress() {
      const rect = root.getBoundingClientRect();
      const maxScroll = root.offsetHeight - window.innerHeight;
      if (maxScroll <= 0) return 0;
      return clamp(-rect.top / maxScroll);
    }

    function seekVideo(progress) {
      if (!hasMetadata || !Number.isFinite(duration)) return;

      const now = performance.now();
      const desiredTime = clamp(progress * duration, 0, Math.max(duration - 0.04, 0));
      const delta = Math.abs(video.currentTime - desiredTime);
      const isLargeJump = delta > 0.55;
      const minSeekGap = isMobile ? 72 : 50;

      if (delta < 0.028) return;
      if (!isLargeJump && now - lastSeekAt < minSeekGap) return;
      if (video.seeking && !isLargeJump && now - lastSeekAt < 120) return;

      lastSeekAt = now;

      if (typeof video.fastSeek === "function" && isLargeJump) {
        video.fastSeek(desiredTime);
      } else {
        video.currentTime = desiredTime;
      }
    }

    function updatePanels(progress, force) {
      if (!force && Math.abs(progress - lastPanelScrollProgress) < 0.002) return;
      lastPanelScrollProgress = progress;

      panels.forEach((panel) => {
        const rect = panel.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const distance = (center - window.innerHeight / 2) / window.innerHeight;
        const speed = Number(panel.dataset.speed || 0.5);
        const opacity = clamp(1 - Math.abs(distance) * 1.18, 0.06, 1);
        const y = distance * -118 * speed;
        const scale = lerp(0.96, 1, opacity);

        panel.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
        panel.style.opacity = opacity.toFixed(3);
      });

      if (splitOverlay) {
        splitOverlay.style.opacity = lerp(1, 0.54, smoothstep(0.35, 0.84, progress)).toFixed(3);
      }
    }

    function updateVideoLayer(progress) {
      const scale = lerp(1.04, 1.13, easeOut(progress));
      const y = lerp(0, isMobile ? -16 : -26, progress);

      video.style.transform = `translate(-50%, calc(-50% + ${y.toFixed(2)}px)) scale(${scale.toFixed(4)})`;
      shade.style.opacity = (isMobile ? lerp(0.72, 0.58, progress) : lerp(0.64, 0.42, progress)).toFixed(3);
    }

    function updateProgressUi(progress) {
      const percent = Math.round(progress * 100);
      meterBar.style.width = `${percent}%`;
      timecode.textContent = `${String(percent).padStart(2, "0")}%`;
    }

    function render() {
      if (!isActive || document.hidden) {
        rafId = null;
        return;
      }

      scrollProgress = getScrollProgress();
      targetVideoProgress = scrollProgress;
      smoothVideoProgress = lerp(smoothVideoProgress, targetVideoProgress, 0.14);

      seekVideo(smoothVideoProgress);
      updateProgressUi(smoothVideoProgress);
      updateVideoLayer(smoothVideoProgress);
      updatePanels(scrollProgress);

      if (Math.abs(smoothVideoProgress - targetVideoProgress) > 0.0007) {
        rafId = requestAnimationFrame(render);
      } else {
        rafId = null;
      }
    }

    function requestRender() {
      if (!isActive || document.hidden) return;
      if (!rafId) rafId = requestAnimationFrame(render);
    }

    function primeVideo() {
      if (hasPrimedVideo) return;
      hasPrimedVideo = true;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise.then(() => {
          video.pause();
          requestRender();
        }).catch(() => {});
      }
    }

    function onMetadataLoaded() {
      duration = Math.max(video.duration || 1, 1);
      hasMetadata = true;
      video.pause();
      video.currentTime = 0;
      loading.classList.add("is-hidden");
      updatePanels(getScrollProgress(), true);
      requestRender();
    }

    function handleViewportChange(entries) {
      const entry = entries[0];
      isActive = Boolean(entry && entry.isIntersecting);
      if (isActive) {
        requestRender();
      } else if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function handleVisibilityChange() {
      if (document.hidden && rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      } else {
        requestRender();
      }
    }

    function handleResize() {
      isMobile = window.matchMedia("(max-width: 720px)").matches;
      requestRender();
    }

    function closeMenu() {
      if (!topNav || !menuToggle) return;
      topNav.classList.remove("is-menu-open");
      menuToggle.setAttribute("aria-expanded", "false");
    }

    function toggleMenu() {
      if (!topNav || !menuToggle) return;
      const isOpen = topNav.classList.toggle("is-menu-open");
      menuToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }

    if (menuToggle) {
      menuToggle.addEventListener("click", (event) => {
        event.preventDefault();
        toggleMenu();
      });
    }

    if (mobileMenu) {
      mobileMenu.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", closeMenu);
      });
    }

    video.addEventListener("loadedmetadata", onMetadataLoaded);
    video.addEventListener("canplay", () => {
      loading.classList.add("is-hidden");
    });
    video.addEventListener("error", () => {
      loading.textContent = "Video failed to load";
    });

    window.addEventListener("scroll", requestRender, { passive: true });
    window.addEventListener("resize", handleResize);
    window.addEventListener("pointerdown", primeVideo, { once: true });
    window.addEventListener("touchstart", primeVideo, { once: true, passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(handleViewportChange, {
        root: null,
        rootMargin: "100% 0px 100% 0px",
        threshold: 0
      });
      observer.observe(root);
    }

    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.load();
    requestRender();
  }

  function initAllVokalScrollFilms() {
    document.querySelectorAll("[data-vokal-scroll-film]").forEach(initVokalScrollFilm);
  }

  document.addEventListener("DOMContentLoaded", initAllVokalScrollFilms);
  document.addEventListener("shopify:section:load", initAllVokalScrollFilms);
  initAllVokalScrollFilms();
})();

(function () {
  function initProductBento(root) {
    if (!root || root.dataset.vokalBentoReady === "true") return;

    const cards = Array.from(root.querySelectorAll("[data-vokal-bento-card]"));
    const lightbox = root.querySelector("[data-vokal-bento-lightbox]");
    const lightboxImage = root.querySelector("[data-vokal-bento-lightbox-image]");
    const closeButton = root.querySelector("[data-vokal-bento-close]");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mobile = window.matchMedia("(max-width: 749px)");

    root.dataset.vokalBentoReady = "true";

    function resetCard(card) {
      card.style.setProperty("--glow-opacity", "0");
      card.style.transform = "translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg)";
    }

    cards.forEach((card) => {
      card.addEventListener("pointermove", (event) => {
        if (mobile.matches || reducedMotion.matches) return;
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const xPercent = (x / rect.width) * 100;
        const yPercent = (y / rect.height) * 100;
        const rotateY = ((x / rect.width) - 0.5) * 3.5;
        const rotateX = ((y / rect.height) - 0.5) * -3.5;

        card.style.setProperty("--glow-x", xPercent + "%");
        card.style.setProperty("--glow-y", yPercent + "%");
        card.style.setProperty("--glow-opacity", "1");
        card.style.transform = "translate3d(0, -2px, 0) rotateX(" + rotateX.toFixed(2) + "deg) rotateY(" + rotateY.toFixed(2) + "deg)";
      });

      card.addEventListener("pointerleave", () => resetCard(card));
    });

    function closeLightbox() {
      if (!lightbox || !lightboxImage) return;
      lightbox.hidden = true;
      lightboxImage.src = "";
      lightboxImage.alt = "";
      document.documentElement.style.overflow = "";
    }

    root.querySelectorAll("[data-vokal-bento-open]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!lightbox || !lightboxImage) return;
        lightboxImage.src = button.dataset.fullImage || "";
        lightboxImage.alt = button.dataset.imageAlt || "";
        lightbox.hidden = false;
        document.documentElement.style.overflow = "hidden";
        if (closeButton) closeButton.focus();
      });
    });

    if (closeButton) closeButton.addEventListener("click", closeLightbox);
    if (lightbox) {
      lightbox.addEventListener("click", (event) => {
        if (event.target === lightbox) closeLightbox();
      });
    }
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && lightbox && !lightbox.hidden) closeLightbox();
    });
  }

  function initAllProductBentos() {
    document.querySelectorAll("[data-vokal-product-bento]").forEach(initProductBento);
  }

  document.addEventListener("DOMContentLoaded", initAllProductBentos);
  document.addEventListener("shopify:section:load", initAllProductBentos);
  initAllProductBentos();
})();
